import { fetchAsDataUrl } from "./AuthImage";
import { swatchImageUrl, attachmentImageUrl } from "./useFabricLibrary";
import { resolveOrderFabrics, decodeEntities } from "./fabricUtils";

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const text = (s) => esc(decodeEntities(s));

const STYLES = `*{-webkit-print-color-adjust:exact;print-color-adjust:exact;box-sizing:border-box}
body{font-family:Arial,sans-serif;padding:28px;color:#18181b;margin:0}
h1{font-size:18px;border-bottom:2px solid #f97316;padding-bottom:10px;margin:0 0 6px}
h2{font-size:12px;text-transform:uppercase;letter-spacing:.08em;color:#52525b;margin:0 0 8px}
.meta{color:#666;font-size:12px;margin-bottom:14px}
.alert{border:3px solid #dc2626;background:#fef2f2;color:#991b1b;font-weight:bold;padding:10px 14px;font-size:15px;margin-bottom:14px}
.fabrics{display:flex;flex-wrap:wrap;gap:10px;margin-bottom:14px}
.fabric{display:flex;gap:12px;align-items:center;border:1px solid #d4d4d8;border-radius:6px;padding:8px;break-inside:avoid;min-width:320px}
.fabric img{width:170px;height:105px;object-fit:cover;border:1px solid #a1a1aa;border-radius:4px}
.nophoto{width:170px;height:105px;border:2px dashed #dc2626;color:#dc2626;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;text-align:center}
.code{font-size:28px;font-weight:800;letter-spacing:.04em;line-height:1.1}
.coll{font-size:13px;font-weight:600;margin-top:2px}
.small{font-size:11px;color:#52525b;margin-top:4px}
.check{border:2px solid #18181b;padding:10px 14px;font-size:14px;margin:4px 0 18px;display:flex;gap:28px;align-items:center;flex-wrap:wrap}
.box{display:inline-block;width:16px;height:16px;border:2px solid #18181b;vertical-align:middle;margin-right:8px}
.content{white-space:pre-wrap;line-height:1.6;font-size:14px}
.mini{width:52px;height:32px;object-fit:cover;vertical-align:middle;border:1px solid #71717a;border-radius:3px;margin:0 6px}
.mini-missing{display:inline-block;border:1px dashed #dc2626;color:#dc2626;font-size:10px;padding:2px 4px;margin:0 6px;vertical-align:middle}`;

function contentHtml(content, refs, imgByKey) {
  let out = "";
  let pos = 0;
  refs.filter((r) => r.end != null).forEach((r) => {
    out += text(content.slice(pos, r.end));
    const img = imgByKey.get(r.key);
    out += img ? `<img class="mini" src="${img}" alt="">` : `<span class="mini-missing">PHOTO ?</span>`;
    pos = r.end;
  });
  return out + text(content.slice(pos));
}

function fabricCard(f, img) {
  const visual = img ? `<img src="${img}" alt="">` : `<div class="nophoto">PHOTO<br>MANQUANTE</div>`;
  const source = f.orderPhoto ? "Photo de la commande" : f.swatch ? "Photo bibliothèque" : "Aucune photo";
  return `<div class="fabric">${visual}<div><div class="code">${esc(f.code)}</div><div class="coll">${esc(f.collection || "Collection inconnue")}</div><div class="small">${f.count > 1 ? `× ${f.count} pièces · ` : ""}${source}</div></div></div>`;
}

export async function printFabricSheet({ title, meta, content, orderId, library, photos, manual }) {
  const win = window.open("", "_blank");
  if (!win) return;
  win.document.write('<p style="font-family:Arial;padding:40px">Préparation de la fiche…</p>');
  const resolved = resolveOrderFabrics(content, library, photos, manual);
  const photoUrl = (f) => (f.orderPhoto ? attachmentImageUrl(orderId, f.orderPhoto) : f.swatch ? swatchImageUrl(f.swatch) : null);
  const fabricImgs = await Promise.all(resolved.fabrics.map((f) => (photoUrl(f) ? fetchAsDataUrl(photoUrl(f)) : null)));
  const generalImgs = await Promise.all(resolved.generalPhotos.map((p) => fetchAsDataUrl(attachmentImageUrl(orderId, p))));
  const imgByKey = new Map(resolved.fabrics.map((f, i) => [f.key, fabricImgs[i]]));

  const cards = resolved.fabrics.map((f, i) => fabricCard(f, fabricImgs[i])).join("")
    + resolved.generalPhotos.map((p, i) => (generalImgs[i] ? `<div class="fabric"><img src="${generalImgs[i]}" alt=""><div><div class="coll">Photo jointe à la commande</div><div class="small">${esc(p.fabric_label || p.original_filename || "")}</div></div></div>` : "")).join("");
  const alert = resolved.alert ? `<div class="alert">&#9888; ${esc(resolved.alert.toUpperCase())} — vérifier la toile avant de lancer la production</div>` : "";
  const fabricsBlock = cards ? `<h2>Toiles à fabriquer</h2><div class="fabrics">${cards}</div>` : "";

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Impression - ${esc(title)}</title><style>${STYLES}</style></head><body>
<h1>${esc(title)}</h1><div class="meta">${esc(meta)} • Imprimé le ${new Date().toLocaleDateString("fr-FR")}</div>
${alert}${fabricsBlock}
<div class="check"><span><span class="box"></span>Coloris vérifié (toile = photo)</span><span>Par : ____________________</span><span>Date : ______________</span></div>
<div class="content">${contentHtml(content, resolved.refs, imgByKey)}</div>
</body></html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
  const images = [...win.document.images];
  await Promise.all(images.map((img) => (img.complete ? null : new Promise((r) => { img.onload = r; img.onerror = r; }))));
  win.focus();
  win.print();
}
